import { useCallback, useEffect, useMemo, useState } from "react";

import { Flex } from "@chakra-ui/react";
import { sortBy } from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useMatch, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { routes } from "config/routes";
import { InboundUnitAddOrigin } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { useHandleProductSelection } from "flows/Inbound/stores/useInboundDropService";
import { useAddProductToSelectedProducts } from "flows/Inbound/stores/useInboundProductSearchService";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { TranslatedProductBySubcategory } from "shared/models/product/types";
import { findTranslatedProductSearchUnit } from "shared/models/productSearchUnit/serializer";
import { UnitType } from "shared/models/productSearchUnit/types";
import { useFetchProductsBySubcategory } from "shared/stores/useProductSearchService";
import {
  ProductSearchActionTypes,
  useProductSearchStore,
} from "shared/stores/useProductSearchStore";
import { Button } from "ui/Button/Button";
import { DropDown } from "ui/DropDown/DropDown";
import { InboundNoEANProductCard } from "ui/InboundNoEANProductCard/InboundNoEANProductCard";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isNullOrUndefined } from "utils/tsHelpers";

const CATEGORIES_WITH_COOS = ["Fruits", "Vegetables"];

const showHandlingUnits = (product: TranslatedProductBySubcategory) => {
  return product.units.filter((unit) => unit.unitType === "handling").length === 1;
};

export function ProductsSearchNoEANProductsListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const matchesDroppingNoEANProductsListPage =
    useMatch(routes.inbound.droppingNoEANProducts)?.pathname === pathname;

  const { sharedListName } = useInboundStore(
    useShallow((state) => ({
      sharedListName: state.sharedListName,
    })),
  );

  useEffect(() => {
    if (matchesDroppingNoEANProductsListPage) return;
    if (!sharedListName) navigate(routes.inbound.root);
  }, [navigate, sharedListName, matchesDroppingNoEANProductsListPage]);

  const { handleProductSelection } = useHandleProductSelection();
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { selectedCategoryName, selectedSubcategoryName, setSearchState, resetTextSearch } =
    useProductSearchStore(
      useShallow((state) => ({
        selectedCategoryName: state.selectedCategoryName,
        selectedSubcategoryName: state.selectedSubcategoryName,
        setSearchState: state.setSearchState,
        resetTextSearch: state.resetTextSearch,
      })),
    );

  const { products, isLoading: areProductsLoading } = useFetchProductsBySubcategory();
  const addProductToSelectedProducts = useAddProductToSelectedProducts();
  const goBackToSubcategories = useCallback(() => {
    setSearchState(ProductSearchActionTypes.ACTIVATE_CATEGORY_SEARCH);
    const to = matchesDroppingNoEANProductsListPage
      ? routes.inbound.droppingManualSearch
      : routes.inbound.manualSearch;
    navigate(to);
  }, [navigate, setSearchState, matchesDroppingNoEANProductsListPage]);

  const showCountryFilter = useMemo(
    () => CATEGORIES_WITH_COOS.includes(selectedCategoryName ?? ""),
    [selectedCategoryName],
  );

  const shouldShowSpinnerModal = areProductsLoading || isNullOrUndefined(products);

  const onChooseUnit = useCallback(
    async (product: TranslatedProductBySubcategory, type: UnitType) => {
      const selectedProduct = findTranslatedProductSearchUnit(product, (p) =>
        p.units.find(({ unitType }) => unitType === type),
      );

      if (!matchesDroppingNoEANProductsListPage) {
        addProductToSelectedProducts(selectedProduct, InboundUnitAddOrigin.NoEANSearch);
        return;
      }

      await handleProductSelection({ product: selectedProduct, resetTextSearch });
    },
    [
      addProductToSelectedProducts,
      handleProductSelection,
      matchesDroppingNoEANProductsListPage,
      resetTextSearch,
    ],
  );

  const translatedLabels = {
    bioLabel: intl.formatMessage({
      id: "components.inbound.search-flow.no-ean-products.bio-label",
    }),
    nonBioLabel: intl.formatMessage({
      id: "components.inbound.search-flow.no-ean-products.non-bio-label",
    }),
    addHandlingUnitLabel: intl.formatMessage({
      id: "components.inbound.search-flow.no-ean-products.add-handling-unit-label",
    }),
    addSingleUnitLabel: intl.formatMessage({
      id: "components.inbound.search-flow.no-ean-products.add-single-unit-label",
    }),
  };
  const bioFilterLabelBio = intl.formatMessage({
    id: "components.inbound.search-flow.no-ean-products.bio-filter.bio",
  });
  const bioFilterLabelNonBio = intl.formatMessage({
    id: "components.inbound.search-flow.no-ean-products.bio-filter.non-bio",
  });

  const [bioFilter, setBioFilter] = useState<string | null>(null);
  const [countryFilter, setCountryFilter] = useState<string | null>(null);

  const productMatchesBioFilter = useCallback(
    (p: TranslatedProductBySubcategory) =>
      isNullOrUndefined(bioFilter) || (bioFilter === "bio") === p.isBio,
    [bioFilter],
  );

  const productMatchesCountryFilter = useCallback(
    (p: TranslatedProductBySubcategory) =>
      isNullOrUndefined(countryFilter) || countryFilter === p.countryOfOriginName,
    [countryFilter],
  );

  const productsFilteredbyBio = useMemo(
    () => products?.filter(productMatchesBioFilter),
    [products, productMatchesBioFilter],
  );

  const productsFilteredbyCountry = useMemo(
    () => products?.filter(productMatchesCountryFilter),
    [products, productMatchesCountryFilter],
  );

  const productsFiltered = useMemo(
    () =>
      products?.filter((p) => {
        return productMatchesBioFilter(p) && productMatchesCountryFilter(p);
      }),
    [products, productMatchesBioFilter, productMatchesCountryFilter],
  );

  const productsFilteredAndSorted = useMemo(
    () => sortBy(productsFiltered, (product) => product.name),
    [productsFiltered],
  );

  const bioFilterOptions = useMemo(
    () =>
      [
        { key: "bio", label: bioFilterLabelBio },
        { key: "nonbio", label: bioFilterLabelNonBio },
      ].filter(({ key }) => productsFilteredbyCountry?.find((p) => p.isBio === (key === "bio"))),
    [bioFilterLabelBio, bioFilterLabelNonBio, productsFilteredbyCountry],
  );

  const countryFilterOptions = useMemo(() => {
    const setOfCountryNames =
      productsFilteredbyBio?.reduce((countries, product) => {
        const countryOfOrigin = product.countryOfOriginName;
        if (countryOfOrigin) {
          countries.add(countryOfOrigin);
        }
        return countries;
      }, new Set<string>()) || new Set<string>();
    return Array.from(setOfCountryNames)
      .sort(new Intl.Collator(appLanguage).compare)
      .map((countryName) => ({
        key: countryName,
        label: countryName,
      }));
  }, [productsFilteredbyBio, appLanguage]);

  const resetFilters = useCallback(() => {
    setBioFilter(null);
    setCountryFilter(null);
  }, []);

  return (
    <Page pos="relative" isFull isBgGrey data-testid="inbound-no-ean-products-page">
      <Flex position="sticky" top="0" zIndex="2" direction="column" width="100%">
        <NavigationHeader
          title={selectedSubcategoryName || ""}
          onClickGoBack={goBackToSubcategories}
        />
        <Flex p="s200" pl="1.25rem" justify="space-between" alignItems="center" bgColor="grey.100">
          <Flex gap="s150">
            <DropDown options={bioFilterOptions} value={bioFilter} onInput={setBioFilter}>
              <FormattedMessage id="components.inbound.search-flow.no-ean-products.bio-filter.label" />
            </DropDown>
            {showCountryFilter && (
              <DropDown
                options={countryFilterOptions}
                value={countryFilter}
                onInput={setCountryFilter}
              >
                <FormattedMessage id="components.inbound.search-flow.no-ean-products.country-filter.label" />
              </DropDown>
            )}
          </Flex>
          <Button
            variant="link"
            onClick={resetFilters}
            color="grey.600"
            textDecoration="none"
            size="lg"
            p="0"
            _hover={{ textDecoration: "none" }}
            _active={{ textDecoration: "none" }}
          >
            <FormattedMessage id="components.inbound.search-flow.no-ean-products.reset-filters" />
          </Button>
        </Flex>
      </Flex>
      <SpinnerModal isOpen={shouldShowSpinnerModal} />
      <Flex direction="column" gap="s200">
        {!shouldShowSpinnerModal &&
          productsFilteredAndSorted.map((p) => {
            const productCardProps = {
              productName: p.name,
              productImageUrl: p.imageUrl,
              productSku: p.sku,
              isProductBio: p.isBio,
              countryOfOriginName: p.countryOfOriginName,
              showAddHandlingUnitButton: showHandlingUnits(p),
              onClickProductCard: (unitType: UnitType) => onChooseUnit(p, unitType),
              ...translatedLabels,
            };
            return <InboundNoEANProductCard key={p.sku} {...productCardProps} />;
          })}
      </Flex>
    </Page>
  );
}
